<template>
  <div class="containers">
    <SectionHeader :title="$t('notifications.many')" />

    <div class="body-content">
      <div class="entries-wrap">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('notifications.channels') }}</h3>
            <div class="header-meta">
              <p>
                <Link to="/notifications/channels/new" class="button button-green button-small button-round">{{
                  $t('notifications.addChannel')
                }}</Link>
              </p>
            </div>
          </header>
          <div class="content-body">
            <LoadingSpinner v-if="loading" />
            <div v-else>
              <div v-if="notification_channels.length === 0" class="alert alert-danger">
                {{ $t('notifications.noChannels') }}
              </div>
              <div v-else>
                <Table>
                  <template #header>
                    <th>{{ $t('notifications.email') }}</th>
                    <th></th>
                  </template>
                  <template #body>
                    <tr v-for="notification_channel in notification_channels" :key="notification_channel.uuid">
                      <td>{{ notification_channel.email }}</td>
                      <td class="table__actions">
                        <Link :to="`/notifications/channels/${notification_channel.uuid}`" class="table__action-button">
                          <span class="material-icons">edit</span>
                        </Link>

                        <button class="table__action-button" @click="deleteChannel(notification_channel)">
                          <span class="material-icons">delete</span>
                        </button>
                      </td>
                    </tr>
                  </template>
                </Table>
              </div>
            </div>
          </div>
        </div>

        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('notifications.notificationTopics') }}</h3>
            <div class="header-meta">
              <Link to="/notifications/topics/new" class="button button-green button-small button-round">{{
                $t('notifications.addTopic')
              }}</Link>
            </div>
          </header>
          <div class="content-body">
            <LoadingSpinner v-if="loading" />
            <template v-else>
              <div v-if="notification_topics.length === 0" class="alert alert-danger">
                {{ $t('notifications.noTopics') }}
              </div>
              <Table v-else>
                <template #header>
                  <th>{{ $t('meta.type') }}</th>

                  <template v-if="companyUser.company_type == 'branch'">
                    <th>
                      {{ $t('notifications.types.fillLevel.matcher') }}
                    </th>
                    <th>{{ $t('containers.value') }}</th>
                    <th>{{ $t('notifications.types.fillLevel.interval') }}</th>
                    <th>{{ $t('meta.project') }}</th>
                  </template>

                  <template v-if="companyUser.company_type == 'industry'">
                    <th>{{ $t('notifications.types.order.orderType') }}</th>
                    <th>{{ $t('notifications.types.order.action') }}</th>
                  </template>

                  <th></th>
                </template>

                <template #body>
                  <tr v-for="notification_topic in notification_topics" :key="notification_topic.uuid">
                    <template v-if="companyUser.company_type == 'branch'">
                      <td>
                        <span>{{ $t('containers.fillLevel') }}</span>
                      </td>

                      <td>
                        <span>
                          {{ $t(`equality.${capitalizeCamelCase(notification_topic.matcher)}`) }}
                        </span>
                      </td>

                      <td>
                        <span>{{ notification_topic.value }} %</span>
                      </td>

                      <td>
                        <span>{{ $t(`notifications.types.fillLevel.intervals.${notification_topic.interval}`) }}</span>
                      </td>

                      <td>
                        <span>{{ notification_topic.project.name }}</span>
                      </td>
                    </template>

                    <template v-if="companyUser.company_type == 'industry'">
                      <td>
                        <span>{{ $t('meta.order') }}</span>
                      </td>

                      <td>
                        <span>
                          {{ $t(`notifications.types.order.orderTypes.${notification_topic.order_type}`) }}
                        </span>
                      </td>

                      <td>
                        <span>
                          {{ $t(`notifications.types.order.actions.${notification_topic.action}`) }}
                        </span>
                      </td>
                    </template>

                    <td class="table__actions">
                      <Link :to="`/notifications/topics/${notification_topic.uuid}`" class="table__action-button">
                        <span class="material-icons">edit</span>
                      </Link>

                      <button @click="deleteTopic(notification_topic)" class="table__action-button">
                        <span class="material-icons actionable">delete</span>
                      </button>
                    </td>
                  </tr>
                </template>
              </Table>
            </template>
          </div>
        </div>

        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('notifications.many') }}</h3>
          </header>

          <div class="content-body">
            <LoadingSpinner v-if="loading" />
            <div v-else>
              <div v-if="notifications.length === 0" class="alert alert-danger">
                {{ $t('notifications.noNotifications') }}
              </div>
              <Table v-else>
                <template #header>
                  <th>{{ $t('notifications.deliveredAt') }}</th>

                  <template v-if="companyUser.company_type == 'branch'">
                    <th>{{ $t('notifications.topic') }}</th>
                    <th>{{ $t('notifications.container') }}</th>
                  </template>

                  <template v-if="companyUser.company_type == 'industry'">
                    <th>{{ $t('meta.order') }}</th>
                    <th>{{ $t('notifications.container') }}</th>
                  </template>
                </template>

                <template #body>
                  <tr v-for="notification in notifications" :key="notification.uuid">
                    <td>{{ formattedFromDateTime(notification.delivered_at) }}</td>

                    <template v-if="companyUser.company_type == 'branch'">
                      <td>
                        <span>{{ $t('containers.fillLevel') }} </span>
                        <span>{{ $t(`equality.${capitalizeCamelCase(notification.topic.matcher)}`) }}</span>
                        {{ notification.topic.value }} %
                      </td>

                      <td>
                        <Link :to="`/containers/${notification.container.uuid}`">
                          {{ notification.container.name }}
                        </Link>
                      </td>
                    </template>

                    <template v-if="companyUser.company_type == 'industry'">
                      <td>
                        <span>
                          {{ $t('meta.order') }}

                          <Link :to="`/orders/${notification.order.uuid}`">
                            {{ $t(`notifications.types.order.orderTypes.${notification.topic.order_type}`) }}
                          </Link>

                          {{ $t(`notifications.types.order.actions.${notification.topic.action}`).toLowerCase() }}
                        </span>
                      </td>

                      <td>
                        <Link :to="`/containers/${notification.container.uuid}`">
                          {{ notification.container.name }}
                        </Link>
                      </td>
                    </template>
                  </tr>
                </template>
              </Table>

              <div v-if="pagination && pagination.totalObjects > 0" class="pagination">
                <pagination
                  :records="pagination.totalObjects"
                  v-model="pagination.page"
                  :per-page="pagination.perPage"
                  @paginate="getData"
                  :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from 'vue-pagination-2'

import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import SectionHeader from '@/components/SectionHeader'
import Table from '@/components/Table/index.vue'
import dateUtils from '@/components/mixins/dateUtils'
import stringUtils from '@/components/mixins/stringUtils'

import notificationChannelsApi from '@/api/notificationChannels'
import notificationTopicsApi from '@/api/notificationTopics'
import notificationsApi from '@/api/notifications'

export default {
  name: 'all_notifications',
  components: { LoadingSpinner, Pagination, Table, SectionHeader },
  inject: ['showPopup', 'companyUser'],
  mixins: [dateUtils, stringUtils],
  data: () => ({
    loading: true,
    notification_channels: [],
    notification_topics: [],
    notifications: [],
    pagination: {
      page: 1,
      totalObjects: 0,
      perPage: 0,
    },
  }),
  methods: {
    async getData(page) {
      page = page || this.pagination.page
      this.loading = true
      const [channels, topics, notificationsResponse] = await Promise.all([
        notificationChannelsApi.getAll(this.companyUser.company_uuid),
        notificationTopicsApi.getAll(this.companyUser.company_uuid),
        notificationsApi.getAll(this.companyUser.company_uuid, { page }),
      ])
      this.notification_channels = channels
      this.notification_topics = topics
      this.notifications = notificationsResponse.notifications
      this.pagination.page = page
      this.pagination.totalObjects = notificationsResponse.pagination?.total_objects
      this.pagination.perPage = notificationsResponse.pagination?.per_page
      this.loading = false
    },
    async deleteChannel(channel) {
      if (await this.showPopup({ content: this.$t('notifications.deleteChannelConfirmation'), type: 'confirm' })) {
        const response = await notificationChannelsApi.delete(channel.uuid)
        if (response.success) {
          await this.getData()
        }
      }
    },
    async deleteTopic(topic) {
      if (await this.showPopup({ content: this.$t('notifications.deleteTopicConfirmation'), type: 'confirm' })) {
        const response = await notificationTopicsApi.delete(topic.uuid)
        if (response.success) {
          await this.getData()
        }
      }
    },
  },
  mounted() {
    this.getData()
  },
}
</script>

<style lang="scss" scoped></style>
